import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { convertTimeDuration, getClosestAspectRatio, formatFileSize, VideoSourceType } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { ItemDetailsPanelBase } from './asset-panel-base/panel-base';
import { InfoPaneHeader } from './asset-panel-base/info-panel-header';
import { DeleteAssetDialog } from './asset-panel-base/delete-asset-dialog';
import { InfoPaneItemCopyUrl } from './basic-info/info-item-copy-url';
import { ItemInfoContentBase } from './basic-info/item-info-panel-content-base';
import { VideoQualityBadge } from './basic-info/info-item-video-quality';

export const VideoDetailsPanel = ({
  isOpen,
  assetInfo,
  onDismiss,
  isReadonly,
  i18n,
  footerKeyDownHandler,
  logger,
  handleDeleteAsset,
  handleRenameAsset,
  copyUrl,
  notificationApi,
  permissions,
  navigation,
}) => {
  const { id: videoId } = assetInfo || {};
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const onDelete = useCallback(() => {
    logger.logTrace('Video Details: delete video', { videoId });
    return handleDeleteAsset();
  }, [logger, videoId, handleDeleteAsset]);

  const onRenameVideo = useCallback((newName) => {
    logger.logTrace('Video Details: rename video', { videoId });
    return handleRenameAsset(newName);
  }, [handleRenameAsset, logger, videoId]);

  const onEditVideo = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('Video Details: edit video', { assetId: assetInfo.id });
    }
    onDismiss();
    navigation.gotoEditVideoPage({ videoId: assetInfo.id });
  }, [onDismiss, navigation, logger]);

  const onCopyUrl = useCallback((url) => {
    logger.logTrace('Video Details: url copied', { videoId });
    copyUrl(url);
  }, [copyUrl, logger, videoId]);

  const formatDate = useCallback(date => i18n.formatDate(date, { date: 'long' }), [i18n]);

  const videoTemplateUIEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;
  const isAIGenerated = assetInfo && assetInfo.sourceType && assetInfo.sourceType === VideoSourceType.AIGenerated;
  const disableEdit = !(videoTemplateUIEnabled && assetInfo && assetInfo.id && assetInfo.metaData && isAIGenerated);

  const videoInfoArr = useMemo(() => {
    const videoInfoList = [
      { name: i18n.getString(_TL_(('Video ID:'))), value: assetInfo.id },
      {
        name: i18n.getString(_TL_('Dimensions:')),
        value: `${assetInfo.width} X ${assetInfo.height}`,
        valid: assetInfo.height && assetInfo.height > 0 && assetInfo.width && assetInfo.width > 0,
      },
      {
        name: i18n.getString(_TL_('Aspect ratio:')),
        value: getClosestAspectRatio(assetInfo.width, assetInfo.height),
        valid: assetInfo.height > 0 && assetInfo.width > 0,
      },
      {
        name: i18n.getString(_TL_('File size:')),
        value: `${formatFileSize(assetInfo.fileSize)} ${i18n.getString(_TL_('MB'))}`,
        valid: assetInfo.fileSize > 0,
      },
      {
        name: i18n.getString(_TL_('Duration:')),
        value: convertTimeDuration(assetInfo.durationInMS),
        valid: assetInfo.durationInMS > 0,
      },
      { name: i18n.getString(_TL_('Modified:')), value: formatDate(assetInfo.modifiedTime) },
      { name: i18n.getString(_TL_('Added:')), value: formatDate(assetInfo.createdTime) },
      {
        CustomComponent: VideoQualityBadge,
        CustomComponentOptions: {
          i18n,
          quality: assetInfo.quality,
        },
        key: assetInfo.quality,
      },
      {
        CustomComponent: InfoPaneItemCopyUrl,
        CustomComponentOptions: {
          i18n,
          url: assetInfo.url,
          onCopy: () => { onCopyUrl(assetInfo.url); },
        },
        key: assetInfo.url,
      },
    ];

    return videoInfoList;
  }, [assetInfo.createdTime, assetInfo.durationInMS, assetInfo.id, assetInfo.modifiedTime, assetInfo.url, formatDate, i18n, onCopyUrl]);

  const dismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const onRenderHeader = useCallback(() => (
    <InfoPaneHeader
      dismiss={dismiss}
      isReadonly={isReadonly}
      i18n={i18n}
      title={i18n.getString(_TL_('Video details'))}
      onDelete={() => { setShowDeleteConfirm(true); }}
      onEdit={onEditVideo}
      disableEdit={disableEdit}
    />
  ), [isReadonly, i18n, dismiss]);

  const closeDialog = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  return (
    <ItemDetailsPanelBase
      onRenderHeader={onRenderHeader}
      i18n={i18n}
      isOpen={isOpen}
      dismiss={dismiss}
      footerKeyDownHandler={footerKeyDownHandler}
    >
      <React.Fragment>
        <ItemInfoContentBase
          itemName={assetInfo.name}
          renameAsync={onRenameVideo}
          promptNameInputLabel={i18n.getString(_TL_('Add video name'))}
          nameInputPlaceholderText={i18n.getString(_TL_('Enter video name'))}
          itemInfos={videoInfoArr}
          isReadonly={isReadonly}
          i18n={i18n}
          renameTitleText={i18n.getString(_TL_('Video name'))}
        />
        <DeleteAssetDialog
          notificationApi={notificationApi}
          onDeleteAsync={onDelete}
          onDeleteSuccess={dismiss}
          confirmText={i18n.getString(_TL_('Are you sure you want to delete this video?'))}
          errorMessageForUsedAsset={i18n.getString(_TL_("You can't delete this video because it's being used by other campaigns, ads, or video extensions."))}
          genericErrorMessage={i18n.getString(_TL_('Your video couldn\'t be deleted. Please try again or contact support.'))}
          i18n={i18n}
          isDisplayed={showDeleteConfirm}
          closeDialog={closeDialog}
        />
      </React.Fragment>
    </ItemDetailsPanelBase>
  );
};

VideoDetailsPanel.propTypes = {
  assetInfo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    metadata: PropTypes.shape({}),
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
  isReadonly: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  footerKeyDownHandler: PropTypes.func,
};

VideoDetailsPanel.defaultProps = {
  isReadonly: false,
  footerKeyDownHandler: _.noop,
};
